import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Music, Quote, ArrowLeft, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialCard = ({ testimonial }) => {
  return (
    <Card className="h-full border-amber-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white">
      <CardContent className="p-8 h-full flex flex-col">
        {/* Large Quote Mark */}
        <div className="mb-4">
          <Quote className="w-12 h-12 text-amber-300 fill-amber-100" />
        </div>
        
        {/* Testimonial Text */}
        <p className="text-gray-700 leading-relaxed flex-grow text-lg italic">
          "{testimonial.text}"
        </p>
        
        {/* Author Info */}
        <div className="border-t border-amber-100 pt-4 mt-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-200 to-amber-300 rounded-full flex items-center justify-center">
              <span className="text-amber-700 font-bold text-lg">
                {testimonial.name.charAt(0)}
              </span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{testimonial.name}</p>
              <p className="text-sm text-amber-600">{testimonial.instrument} • {testimonial.duration}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API}/testimonials`);
      const data = await response.json();
      setTestimonials(data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-100 sticky top-0 z-50">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Music className="w-8 h-8 text-amber-600" />
            <span className="text-xl font-bold text-gray-800">Guitar 101 Tuition</span>
          </a>
          <Button 
            onClick={() => window.location.href = '/'}
            variant="outline" 
            className="border-amber-600 text-amber-600 hover:bg-amber-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            What Our <span className="text-amber-600">Students Say</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Don't just take our word for it. Here's what our students and their families 
            have to say about their experience with Guitar 101 Tuition.
          </p>
        </div>
      </section>

      {/* Testimonials Grid or Coming Soon */}
      <section className="py-8 px-4 pb-20">
        <div className="container mx-auto">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
            </div>
          ) : testimonials.length === 0 ? (
            /* Coming Soon State */
            <div className="max-w-2xl mx-auto text-center py-16">
              <div className="bg-white rounded-2xl shadow-xl p-12 border border-amber-100">
                <Quote className="w-20 h-20 text-amber-200 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Coming Soon
                </h2>
                <p className="text-gray-600 text-lg mb-8">
                  We're gathering testimonials from our amazing students. 
                  Check back soon to hear about their musical journeys!
                </p>
                <Button 
                  onClick={() => window.location.href = '/#booking'}
                  className="bg-amber-600 hover:bg-amber-700 text-lg px-8 py-6"
                >
                  Book Your Free Trial Lesson
                </Button>
              </div>
            </div>
          ) : (
            /* Testimonials Grid */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section - Only show if testimonials exist */}
      {!loading && testimonials.length > 0 && (
        <section className="py-16 px-4 bg-amber-600">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Start Your Musical Journey?
            </h2>
            <p className="text-amber-100 text-lg mb-8 max-w-2xl mx-auto">
              Join our community of happy students and discover the joy of making music.
            </p>
            <Button 
              onClick={() => window.location.href = '/#booking'}
              className="bg-white text-amber-600 hover:bg-amber-50 text-lg px-8 py-6"
            >
              Book Your Free Trial Lesson
            </Button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Music className="w-6 h-6 text-amber-500" />
            <span className="text-lg font-semibold">Guitar 101 Tuition</span>
          </div>
          <p className="text-gray-400">
            © {new Date().getFullYear()} Guitar 101 Tuition. All rights reserved.
          </p>
          <a 
            href="/admin" 
            className="mt-4 inline-block text-gray-500 hover:text-amber-500 transition-colors text-sm"
          >
            Admin Login
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Testimonials;
